import React, { useState, useEffect, useRef } from "react";
import { TooltipV2 } from "oolib";
import { Tag } from "oolib/dist/v2/components/Tags";
import { icons } from "oolib/dist/icons";
import { StyledTagsContainer } from "@/components/dataviz/MetricsCard/styled";
import {
  createFilterTooltipContent,
  createHiddenTooltipContent,
  createBreakdownTooltipContent,
} from "./widgetMetadataTagsTooltipConfig";
import { WidgetMetadataTagsProps } from "../../../types";
import { IconButton } from "../styled";
import { resolveHidden } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/utils/visibilityState";
import { useReportLandingContext } from "@/components/analysisModules/reports/ReportLanding/ReportLandingContext";

const TAG_WIDTHS = {
  filter: 100,
  breakdown: 110,
  hidden: 90,
  gap: 8,
};

const useCompactMode = (
  tagsContainerRef: React.RefObject<HTMLDivElement>,
  contentWidth: number
) => {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const element = tagsContainerRef?.current;
    if (!element) return;

    const checkWidth = () => {
      const availableWidth = element.getBoundingClientRect().width;
      setIsCompact(availableWidth < contentWidth);
    };

    checkWidth();

    const resizeObserver = new ResizeObserver(checkWidth);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [tagsContainerRef, contentWidth]);

  return isCompact;
};

export const WidgetMetadataTags: React.FC<WidgetMetadataTagsProps> = ({
  allActiveFilters,
  dim1Visibility,
  dim2Visibility,
  widgetId,
  breakdownCategoryName,
}) => {
  const tagsContainerRef = useRef<HTMLDivElement>(null);

  // The hidden lists these tags describe. In manual mode that's the stored
  // selection; in dynamic mode it's recomputed from the live chart data, so
  // the count can't go stale. Every widget gets a state object from the
  // renderer, so there's no per-widget special-casing here.
  const { widgetDataStore } = useReportLandingContext() || {};
  const liveData = widgetId ? widgetDataStore?.[widgetId] : undefined;

  const effectiveHiddenDim1 = resolveHidden({ state: dim1Visibility, data: liveData, dimension: 1 });
  const effectiveHiddenDim2 = resolveHidden({ state: dim2Visibility, data: liveData, dimension: 2 });

  const DisplayIcon = ({ icon, size, weight = "regular" }) => {
    const IconComp = icons[icon];
    return (
      <>{IconComp && <IconComp size={size} weight={weight} />}</>
    );
  };

  const filters =
    allActiveFilters?.map((filter) => ({
      label: filter.display || "Filter",
      values: Array.isArray(filter.values)
        ? filter.values.map((v) => v?.display || v)
        : [],
    })) || [];

  const filterCount = filters.length;

  const hiddenDim1Count = effectiveHiddenDim1?.length || 0;
  const hiddenDim2Count = effectiveHiddenDim2?.length || 0;
  const totalHiddenCount = hiddenDim1Count + hiddenDim2Count;

  // Calculate total content width
  const hasFilters = filterCount > 0;
  const hasBreakdown = !!breakdownCategoryName;
  const hasHidden = totalHiddenCount > 0;
  const tagCount = (hasFilters ? 1 : 0) + (hasBreakdown ? 1 : 0) + (hasHidden ? 1 : 0);

  const totalContentWidth =
    (hasFilters ? TAG_WIDTHS.filter : 0) +
    (hasBreakdown ? TAG_WIDTHS.breakdown : 0) +
    (hasHidden ? TAG_WIDTHS.hidden : 0) +
    (tagCount > 1 ? (tagCount - 1) * TAG_WIDTHS.gap : 0);

  const isCompact = useCompactMode(tagsContainerRef, totalContentWidth);

  if (filterCount === 0 && totalHiddenCount === 0 && !breakdownCategoryName) {
    return null;
  }

  return (
    <StyledTagsContainer ref={tagsContainerRef} style={{ width: "100%" }}>
      {filterCount > 0 && (
        <TooltipV2
          primaryContent={createFilterTooltipContent(filters)}
          trigger="click"
          placement="bottom"
        >
          {isCompact ? (
            <IconButton>
              <DisplayIcon icon="Funnel" size={12} weight="bold" />
            </IconButton>
          ) : (
            <Tag
              display={`${filterCount} Filter${filterCount > 1 ? "s" : ""} Applied`}
              size="XS"
              variant="link"
              style={{ cursor: "pointer" }}
            />
          )}
        </TooltipV2>
      )}
      {breakdownCategoryName && (
        <TooltipV2
          primaryContent={createBreakdownTooltipContent(breakdownCategoryName)}
          trigger="click"
          placement="bottom"
        >
          {isCompact ? (
            <IconButton>
              <DisplayIcon icon="ChartBarHorizontal" size={12} weight="bold" />
            </IconButton>
          ) : (
            <Tag
              display="Breakdown Applied"
              size="XS"
              variant="link"
              style={{ cursor: "pointer" }}
            />
          )}
        </TooltipV2>
      )}
      {totalHiddenCount > 0 && (
        <TooltipV2
          primaryContent={createHiddenTooltipContent(
            effectiveHiddenDim1,
            effectiveHiddenDim2
          )}
          trigger="click"
          placement="bottom"
        >
          {isCompact ? (
            <IconButton>
              <DisplayIcon icon="EyeClosed" size={12} weight="bold" />
            </IconButton>
          ) : (
            <Tag
              display={`${totalHiddenCount} Hidden Bar${totalHiddenCount > 1 ? "s" : ""}`}
              size="XS"
              variant="link"
              style={{ cursor: "pointer" }}
            />
          )}
        </TooltipV2>
      )}
    </StyledTagsContainer>
  );
};
